import React, { useState, useEffect, useCallback, memo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const UpdateBranch = memo(() => {
  const { id } = useParams();
  const [branch, setBranch] = useState({});
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const fetchBranch = useCallback(async () => {
    try {
      const response = await axios.get(`/api/branch/branch/${id}`);
      setBranch(response.data);
    } catch (err) {
      setError("Error fetching branch details");
      console.error("Fetch branch error:", err);
    }
  }, [id]);

  const fetchAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const response = await axios.get(`/api/user/branch`);
      setAgents(response.data); // Directly set the array response
    } catch (err) {
      setError("Error fetching agents");
      console.error("Fetch agents error:", err);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  useEffect(() => {
    fetchBranch();
    fetchAgents();
  }, [fetchBranch, fetchAgents]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setBranch(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `/api/branch/updatebranch/${id}`,
        branch
      );
      if (response.data) {
        setError(response.data.message || "Update successful");
      } else {
        setError("Update failed. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error updating branch");
      console.error("Update error:", err);
    } finally {
      setLoading(false);
    }
  }, [branch, id]);

  return (
    <div className="tw-bg-slate-100 tw-flex tw-justify-center tw-items-center tw-min-h-screen">
      <div className="tw-w-full tw-max-w-md tw-p-8 tw-rounded-md tw-shadow-md tw-border-l-8 tw-border-l-blue-600 tw-bg-white">
        <h2 className="tw-text-lg tw-font-semibold tw-mb-4">Edit Branch</h2>
        {error && <p className="tw-text-red-500 tw-mb-4">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="name">
              Name
            </label>
            <input
              className="tw-w-full tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-gray-300 focus:tw-outline-none focus:tw-ring focus:tw-ring-purple-500 focus:tw-ring-opacity-50"
              type="text"
              name="name"
              value={branch.name || ""}
              onChange={handleChange}
              required
            />
          </div>

          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="location">
              Location
            </label>
            <input
              className="tw-w-full tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-gray-300 focus:tw-outline-none focus:tw-ring focus:tw-ring-purple-500 focus:tw-ring-opacity-50"
              type="text"
              name="location"
              value={branch.location || ""}
              onChange={handleChange}
              required
            />
          </div>

          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="agent">
              Agent
            </label>
            <select
              name="agent"
              value={branch.agent || ""}
              onChange={handleChange}
              className="tw-dark:bg-slate-100 tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5"
              required
              disabled={loadingAgents}
            >
              <option value="">Select Agent</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent.username}>
                  {agent.username}
                </option>
              ))}
            </select>
            {loadingAgents && (
              <p className="tw-mt-2 tw-text-sm tw-text-gray-500">Loading agents...</p>
            )}
          </div>

          <div className="tw-flex tw-justify-end tw-mt-4">
            <button
              type="submit"
              disabled={loading || loadingAgents}
              className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring focus:tw-ring-purple-500 focus:tw-ring-opacity-50 disabled:tw-opacity-50"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default UpdateBranch;